import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Receipt } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import Pagination, { paginate } from "@/components/Pagination";
import { toast } from "sonner";

const CATEGORIES = ["Maintenance", "Réparation", "Taxe", "Eau", "Électricité", "Sécurité", "Autre"];
const empty = { maison_id: "", categorie: "Maintenance", montant: 0, date: "", description: "" };

export default function Depenses() {
  const [items, setItems] = useState([]);
  const [maisons, setMaisons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    const [d, m] = await Promise.all([api.get("/depenses"), api.get("/maisons")]);
    setItems(d.data); setMaisons(m.data);
  };
  useEffect(() => { load(); }, []);

  const maisonName = (id) => maisons.find((x) => x.id === id)?.nom || "—";

  const filteredItems = items.filter((d) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (d.categorie || "").toLowerCase().includes(q) ||
      (d.description || "").toLowerCase().includes(q) ||
      (maisonName(d.maison_id) || "").toLowerCase().includes(q)
    );
  });
  const pag = paginate(filteredItems, page);
  useEffect(() => { setPage(1); }, [search]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        montant: Number(form.montant || 0),
        maison_id: form.maison_id || null,
        date: form.date ? new Date(form.date).toISOString() : null,
      };
      if (editing) await api.put(`/depenses/${editing}`, payload);
      else await api.post("/depenses", payload);
      toast.success(editing ? "Dépense modifiée" : "Dépense ajoutée");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };
  const onEdit = (it) => {
    setEditing(it.id);
    setForm({ ...empty, ...it, maison_id: it.maison_id || "", date: it.date?.slice(0,10) || "" });
    setOpen(true);
  };
  const onDelete = async () => {
    try { await api.delete(`/depenses/${confirmId}`); toast.success("Supprimée"); setConfirmId(null); load(); }
    catch { toast.error("Suppression échouée"); }
  };

  const total = items.reduce((s, x) => s + (x.montant || 0), 0);

  return (
    <div>
      <PageHeader
        title="Dépenses"
        subtitle="Suivi des frais liés à vos biens : maintenance, réparations, taxes et charges."
        testid="page-depenses"
        accent="rose"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher dépense…" testid="search-depenses" />
            <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-900/20" data-testid="btn-add-depense">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle dépense
            </Button>
          </div>
        }
      />

      <Card className="p-5 rounded-xl border border-rose-200 shadow-sm bg-gradient-to-br from-rose-50 to-white mb-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-rose-700">Total enregistré</div>
          <div className="font-heading text-3xl font-bold text-rose-900 mt-1">{formatFCFA(total)}</div>
        </div>
        <Receipt className="w-10 h-10 text-rose-500/40" />
      </Card>

      <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Catégorie</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maison</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Description</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Montant</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-12">{search ? `Aucun résultat pour « ${search} ».` : "Aucune dépense."}</TableCell></TableRow>
            )}
            {pag.rows.map((d) => (
              <TableRow key={d.id} className="hover:bg-slate-50/60">
                <TableCell className="text-slate-600">{formatDate(d.date)}</TableCell>
                <TableCell className="text-slate-900 font-medium">{d.categorie}</TableCell>
                <TableCell className="text-slate-600">{maisonName(d.maison_id)}</TableCell>
                <TableCell className="text-slate-600">{d.description || "—"}</TableCell>
                <TableCell className="text-right font-medium text-rose-700">{formatFCFA(d.montant)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(d)}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmId(d.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-depenses" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-depense">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier dépense" : "Nouvelle dépense"}</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Maison</Label>
                <Select value={form.maison_id || "_none"} onValueChange={(v) => setForm({ ...form, maison_id: v === "_none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Aucune (général)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Aucune (général)</SelectItem>
                    {maisons.map((m) => <SelectItem key={m.id} value={m.id}>{m.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Montant (FCFA) *</Label>
                <Input type="number" min="0" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} required data-testid="input-depense-montant" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-depense">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
